import { Dexterity, IBfMetricsDisplayData, IStrikeZoneConfig, UnitSystem, metersToInches } from '@trackman/baseball-shared-components';
import { IGame } from 'interfaces/api/IGame';
import { Decision, IStrikeZoneDecisionModel } from 'interfaces/session/IStrikeZoneDecision';
import { StrikeZoneLocation } from 'interfaces/session/IStrikeZoneDefinition';

export type StrikeZoneConfig = IStrikeZoneConfig & { name: string };

const formatValue = (value: number, isImperial: boolean) => (isImperial ? metersToInches(value) : (value * 100).toFixed(2));

const inchesToMeters = (value: number) => {
  return value * 0.0254;
};

export const getStrikeZoneDecision = (strikeZoneDecision: IStrikeZoneDecisionModel): Decision | undefined => {
  if (!strikeZoneDecision) {
    return undefined;
  }

  const priorityOrder = [StrikeZoneLocation.Front, StrikeZoneLocation.Middle, StrikeZoneLocation.Back];

  const data = strikeZoneDecision.strikeZoneDecisionLocations.find((location) => priorityOrder.includes(location.strikeZoneLocationType));

  return data ? data : strikeZoneDecision;
};

// Derived on every render (not kept in state) so the drawn zone can never lag behind the play shown with it.
// Only the newest play's decision is used: until it arrives the game's static zone is drawn, never the zone of a
// previous play (and so of a previous batter).
export const getStrikeZoneConfig = (
  lastDisplayPlay: IBfMetricsDisplayData | undefined,
  game: IGame | undefined,
  unitSystem: UnitSystem
): StrikeZoneConfig | undefined => {
  const isImperial = unitSystem === UnitSystem.Imperial;

  if (lastDisplayPlay?.strikeZoneDecision) {
    const { playerAtBat, strikeZoneType, dimensions } = lastDisplayPlay.strikeZoneDecision;
    const { top, bottom, left, right, ballRadius } = dimensions;

    const data = getStrikeZoneDecision(lastDisplayPlay.strikeZoneDecision as IStrikeZoneDecisionModel);

    let config: any;

    if (data) {
      config = {
        strikeZoneType: lastDisplayPlay.strikeZoneDecision.strikeZone.customDefinition
          ? lastDisplayPlay.strikeZoneDecision.strikeZone.customDefinition.activeStrikeZone
          : lastDisplayPlay.strikeZoneDecision.strikeZone.activeStrikeZone,
        top: data.dimensions.top,
        bottom: data.dimensions.bottom,
        left: data.dimensions.left,
        right: data.dimensions.right,
        ballRadius: data.dimensions.ballRadius,
        topStrikeZoneValue: formatValue(data.dimensions.top, isImperial),
        bottomStrikeZoneValue: formatValue(data.dimensions.bottom, isImperial),
      };
    } else {
      config = {
        strikeZoneType,
        top,
        bottom,
        left,
        right,
        ballRadius,
        topStrikeZoneValue: formatValue(top, isImperial),
        bottomStrikeZoneValue: formatValue(bottom, isImperial),
      };
    }

    return {
      batterHandedness: playerAtBat?.battingHandedness ?? Dexterity.Right,
      name: '',
      ...config,
    };
  }

  const values = game?.strikeZoneDefinition;

  if (!values) {
    return undefined;
  }

  return {
    name: '',
    left: values.staticStrikeZoneWidth ? -inchesToMeters(values.staticStrikeZoneWidth / 2) : 0.037338,
    right: values.staticStrikeZoneWidth ? inchesToMeters(values.staticStrikeZoneWidth / 2) : 0.501904,
    top: values.staticStrikeZoneTop ? inchesToMeters(values.staticStrikeZoneTop) : -0.2159,
    bottom: values.staticStrikeZoneBottom ? inchesToMeters(values.staticStrikeZoneBottom) : 0.2159,
    ballRadius: values.ballDiameter ? inchesToMeters(values.ballDiameter / 2) : 1.082294,
    batterHandedness: Dexterity.Right,
    strikeZoneType: values.activeStrikeZone,
    topStrikeZoneValue: formatValue(inchesToMeters(values.staticStrikeZoneTop ? values.staticStrikeZoneTop : -0.2159), isImperial),
    bottomStrikeZoneValue: formatValue(inchesToMeters(values.staticStrikeZoneBottom ? values.staticStrikeZoneBottom : 0.2159), isImperial),
  };
};
